import type { ReactNode } from "react";
import { X } from "lucide-react";
import { cn } from "../../lib/cn";

export interface BulkActionBarProps {
  count: number;
  /** Singular noun, e.g. "contact" — pluralised here so callers don't each reinvent it. */
  noun: string;
  onClear: () => void;
  children: ReactNode;
  className?: string;
}

/**
 * Appears above a table once rows are selected, and takes the place of the filter row rather than
 * pushing it down — a bar that inserted itself would shift every row under the cursor mid-click.
 *
 * Rendered only when count > 0, so it costs nothing at rest.
 */
export function BulkActionBar({ count, noun, onClear, children, className }: BulkActionBarProps) {
  return (
    <div
      role="region"
      aria-label={`${count} ${noun}${count === 1 ? "" : "s"} selected`}
      className={cn(
        "flex flex-wrap items-center gap-3 rounded-card border border-accent/30 bg-accent-soft p-3",
        className,
      )}
    >
      <span className="text-sm font-medium text-text">
        {count} {noun}
        {count === 1 ? "" : "s"} selected
      </span>

      <div className="flex flex-wrap items-center gap-2">{children}</div>

      <button
        type="button"
        onClick={onClear}
        className={cn(
          "ml-auto inline-flex items-center gap-1 rounded-control px-2 py-1 text-xs font-medium",
          "text-text-muted transition-colors duration-fast ease-standard",
          "hover:bg-surface-hover hover:text-text",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
        )}
      >
        <X className="size-3.5" aria-hidden />
        Clear
      </button>
    </div>
  );
}
